import type { Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

/**
 * The corner radius of a filter dropdown, taken FROM the field it drops out of.
 *
 * `@repo/ui`'s `Input` rounds to 8px (`packages/ui/components/input/Input/Input.tsx`), and every
 * filter field's control is one — so the panel that opens directly beneath it has to round the
 * same amount or the two read as unrelated surfaces stacked on each other. They previously did
 * not: the dropdowns were 16px and the calendar 12px, both visibly rounder than the 8px search
 * box they hang off, which is exactly the mismatch this constant removes.
 *
 * Derived, not chosen. If the Input's radius ever changes, this is the one line to follow it.
 */
export const FILTER_DROPDOWN_RADIUS = '8px'

/**
 * The gap the panel leaves under its field — the `mt` below, as a number so the
 * available-height arithmetic can subtract it.
 */
const FILTER_DROPDOWN_GAP = 8

/**
 * MUI `Popover`'s own `marginThreshold` default. Its `getPositioningStyle` refuses to let the
 * paper come within this many pixels of the viewport edge, and that constant is what decides
 * whether the panel gets yanked upward — so the height budget below has to use the same number.
 */
const POPOVER_MARGIN_THRESHOLD = 16

/**
 * Never squeeze the panel below this. See `filterDropdownMaxHeight` for the trade this makes.
 */
const FILTER_DROPDOWN_MIN_HEIGHT = 120

/**
 * How tall the panel may be and still sit BENEATH `anchorEl` rather than on top of it.
 *
 * THE BUG THIS EXISTS FOR (VERA-304). The recipient picker in `TransferCertificateModal` rendered
 * its results OVER its own search field. It was not a flip — MUI `Popover` has no flip modifier,
 * unlike `Popper`. It is `getPositioningStyle`'s viewport CLAMP: when the paper would overflow the
 * bottom it rewrites `top` to `viewportHeight - marginThreshold - paperHeight`, walking the panel
 * up the screen until it fits, straight over the anchor. Measured live at 1280x800, field bottom
 * 498.5 and a 338px paper: MUI computed `top: 446px`, which put the panel 44.5px above the field's
 * own bottom edge and buried the input the user was still typing into.
 *
 * So capping the height is not a cosmetic "make the list shorter" — it is what removes the clamp's
 * trigger condition. A panel that fits below is never repositioned, because the branch that would
 * move it is never taken. Shrinking alone (a fixed `maxHeight: 400`) would NOT have fixed this:
 * 400 still overflows from y=498, so it would still have been shoved over the field.
 *
 * MUI's own `maxHeight: calc(100% - 32px)` does not help, because it is measured against the
 * VIEWPORT, not against the space below the anchor. It caps a panel at 768px on this screen while
 * only 277px of room exists underneath the field.
 *
 * HONEST LIMIT: on a viewport short enough that even `FILTER_DROPDOWN_MIN_HEIGHT` will not fit
 * below the field, the floor wins and MUI clamps again. That is deliberate — a 20px scroll
 * viewport showing a third of one row is worse than an overlap — but it does mean this is a fix
 * for the realistic case, not a proof for every geometry. Opening deliberately upward is the
 * complete answer and is a larger change than this bug warrants.
 */
export const filterDropdownMaxHeight = (anchorEl?: HTMLElement | null): number | undefined => {
  if (!anchorEl || typeof window === 'undefined') return undefined
  const below =
    window.innerHeight -
    anchorEl.getBoundingClientRect().bottom -
    FILTER_DROPDOWN_GAP -
    POPOVER_MARGIN_THRESHOLD
  return Math.max(below, FILTER_DROPDOWN_MIN_HEIGHT)
}

/**
 * The shared paper treatment for a filter dropdown that opens under a field.
 *
 * `SearchFilter` and `CompanyFilter` rendered byte-identical `Popover` papers — CompanyFilter's
 * own comment called itself "a copy of the SearchFilter dropdown" — and the two had already
 * started to be edited in lockstep by hand, one commit at a time. Per working-conventions §36 the
 * second hand-roll becomes a shared thing, so it lives here and both call it.
 *
 * `width` matches the anchored field so the panel lines up with what it belongs to. `anchorEl`
 * is what the height budget is measured from — pass the same element given to the `Popover`.
 */
export const filterDropdownPaperSx = (
  theme: Theme,
  width?: number,
  anchorEl?: HTMLElement | null,
): SystemStyleObject<Theme> => ({
  // 8px inner gutter, so the rows sit inset from the panel edge instead of running
  // edge-to-edge into the corners.
  p: '8px',
  borderRadius: FILTER_DROPDOWN_RADIUS,
  // The same gap a `SelectField` menu leaves below its field, so every dropdown in the app
  // sits the same distance under the control that opened it. 8px, not the previous 6px: the
  // filter panel's options sat too tight under their search input, and 6 also disagreed with
  // the 8px every `MenuButton` menu already used, so the app carried two dropdown gaps.
  // Raising this one means raising `SelectField` and `Select` in the same beat, or the
  // parity claimed in the sentence above stops being true.
  mt: `${FILTER_DROPDOWN_GAP}px`,
  width,
  // Beats MUI's own viewport-relative `calc(100% - 32px)`, which is the value that let the panel
  // grow tall enough to be clamped over its field. `!important` is not needed — this sx lands on
  // the same element and wins on specificity/order.
  maxHeight: filterDropdownMaxHeight(anchorEl),
  overflowX: 'hidden',
  // The panel scrolls its own overflow rather than growing. MUI already sets `overflow-y: auto`
  // here, but it is stated explicitly because the `maxHeight` above is what makes it load-bearing:
  // without scroll, a capped panel would simply hide the rows past the cap — including the invite
  // footer row rendered inside the same list.
  overflowY: 'auto',
  // Kill MUI's dark-mode elevation overlay so the panel reads as the true `background.paper`
  // rather than a lighter floating card.
  backgroundImage: 'none',
  backgroundColor: 'background.paper',
  // ...which is exactly why it needs an explicit edge: the drawer BEHIND it is forced to
  // `background.paper` too, so in dark mode the two surfaces met invisibly and the dropdown had
  // no perceptible boundary. This is a `Popover`, so it never inherited the border the theme's
  // `MuiMenu` paper override gives every real menu — that override's value is reused verbatim
  // here rather than a new one invented, and `divider` is theme-aware, so light mode simply
  // gains the same subtle edge its other menus already have.
  border: `1px solid ${theme.palette.divider}`,
})
